import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { AppShell } from "@/components/AppShell";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { StatusBadge } from "@/components/StatusBadge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Target, Wallet, User, Repeat, Calendar } from "lucide-react";

const BIDANG_LABEL = {
  BPK: "BPK Klasis",
  PELAYANAN: "Pelayanan",
  ORGANISASI: "Organisasi & Tata Kelola",
  PENGINJILAN: "Penginjilan & Misi",
  DOA_PUASA: "Doa & Puasa",
  BINA_USAHA: "Bina Usaha",
  PERIA: "PERIA", PERNITA: "PERNITA", PEREDA: "PEREDA", PERSEMI: "PERSEMI",
};

function formatIDR(n) {
  return n ? new Intl.NumberFormat("id-ID", { style: "currency", currency: "IDR", maximumFractionDigits: 0 }).format(n) : "—";
}

export default function ProgramKerja() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [year, setYear] = useState("ALL");

  useEffect(() => {
    api.get("/programs").then((r) => setItems(r.data)).finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => items.filter((p) =>
    (year === "ALL" || String(p.year) === year)
  ), [items, year]);

  const grouped = useMemo(() => {
    const g = {};
    filtered.forEach((p) => { (g[p.bidang] = g[p.bidang] || []).push(p); });
    return g;
  }, [filtered]);

  const totalBudget = filtered.reduce((s, p) => s + (p.budget || 0), 0);
  const totalRealisasi = filtered.reduce((s, p) => s + (p.realisasi || 0), 0);
  const years = [...new Set(items.map((p) => p.year).filter(Boolean))].sort();

  return (
    <AppShell>
      <div className="mb-6">
        <div className="text-xs tracking-[0.18em] uppercase text-muted-foreground">Pelayanan</div>
        <h1 className="font-heading font-bold text-3xl mt-1">Program Kerja Klasis</h1>
        <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
          Program kerja periode <strong>2026–2031</strong> hasil RAKER BPK Klasis Mimika 22 Agustus 2026 di GPDP Jemaat El Olam.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Card><CardContent className="p-5">
          <div className="text-[0.65rem] tracking-[0.18em] uppercase text-muted-foreground">Total Program</div>
          <div className="font-heading font-bold text-3xl mt-1">{filtered.length}</div>
        </CardContent></Card>
        <Card><CardContent className="p-5">
          <div className="text-[0.65rem] tracking-[0.18em] uppercase text-muted-foreground">Anggaran Total</div>
          <div className="font-heading font-bold text-lg mt-1 text-primary">{formatIDR(totalBudget)}</div>
        </CardContent></Card>
        <Card><CardContent className="p-5">
          <div className="text-[0.65rem] tracking-[0.18em] uppercase text-muted-foreground">Realisasi</div>
          <div className="font-heading font-bold text-lg mt-1 text-accent">{formatIDR(totalRealisasi)}</div>
          <div className="text-[0.65rem] text-muted-foreground">{totalBudget > 0 ? Math.round(totalRealisasi*100/totalBudget) : 0}% dari anggaran</div>
        </CardContent></Card>
        <Card><CardContent className="p-5">
          <div className="text-[0.65rem] tracking-[0.18em] uppercase text-muted-foreground">Potensi Kekurangan</div>
          <div className="font-heading font-bold text-lg mt-1 text-secondary">Rp 1.103.100.000</div>
          <div className="text-[0.65rem] text-muted-foreground">Target Bina Usaha</div>
        </CardContent></Card>
      </div>

      <div className="flex flex-wrap gap-3 mb-4">
        <Tabs value={year} onValueChange={setYear}>
          <TabsList>
            <TabsTrigger value="ALL">Semua Tahun</TabsTrigger>
            {years.map((y) => <TabsTrigger key={y} value={String(y)}>{y}</TabsTrigger>)}
          </TabsList>
        </Tabs>
      </div>

      {loading ? <div className="text-sm text-muted-foreground">Memuat…</div> : filtered.length === 0 ? (
        <div className="border border-dashed border-border rounded-lg p-12 text-center">
          <Target className="h-8 w-8 mx-auto text-muted-foreground mb-3" />
          <div className="text-sm text-muted-foreground">Belum ada program kerja pada filter ini.</div>
        </div>
      ) : (
        <div className="space-y-8">
          {Object.entries(grouped).map(([b, list]) => (
            <div key={b}>
              <div className="flex items-center gap-2 mb-3">
                <div className="h-1 w-8 rounded-full bg-accent" />
                <div className="font-heading font-bold text-sm tracking-wide text-primary">{BIDANG_LABEL[b] || b}</div>
                <div className="text-xs text-muted-foreground">({list.length} program)</div>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                {list.map((p) => (
                  <Card key={p.id} className="card-hover" data-testid={`program-${p.id}`}>
                    <CardContent className="p-5">
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <div className="font-heading font-bold text-base leading-tight">{p.name}</div>
                        <StatusBadge status={p.status} />
                      </div>
                      {p.tujuan && <div className="text-xs text-muted-foreground mt-1">{p.tujuan}</div>}
                      <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
                        {p.pic && <div className="flex items-center gap-1.5 text-muted-foreground"><User className="h-3 w-3 text-primary/60"/> <strong className="text-foreground/80">{p.pic}</strong></div>}
                        {p.year && <div className="flex items-center gap-1.5 text-muted-foreground"><Calendar className="h-3 w-3 text-primary/60"/> Tahun {p.year}</div>}
                        {p.frequency != null && <div className="flex items-center gap-1.5 text-muted-foreground"><Repeat className="h-3 w-3 text-primary/60"/> {p.frequency}× per tahun</div>}
                        {p.budget != null && <div className="flex items-center gap-1.5 text-muted-foreground"><Wallet className="h-3 w-3 text-accent"/> <strong className="text-foreground/80">{formatIDR(p.budget)}</strong></div>}
                      </div>
                      {p.budget != null && (
                        <div className="mt-3 pt-3 border-t border-border">
                          <div className="flex items-center justify-between text-[0.65rem] uppercase tracking-wider text-muted-foreground mb-1">
                            <span>Realisasi Anggaran</span>
                            <span className="font-mono font-semibold text-primary">{formatIDR(p.realisasi || 0)} / {formatIDR(p.budget)}</span>
                          </div>
                          <Progress value={p.budget > 0 ? Math.min(100, (p.realisasi || 0) * 100 / p.budget) : 0} className="h-1.5" />
                        </div>
                      )}
                      {p.indikator && <div className="mt-3 text-[0.7rem] text-muted-foreground border-t border-border pt-2"><strong>Indikator:</strong> {p.indikator}</div>}
                      {typeof p.progress === "number" && (
                        <div className="mt-3">
                          <div className="flex items-center justify-between text-[0.65rem] uppercase tracking-wider text-muted-foreground mb-1">
                            <span>Progress</span><span>{p.progress}%</span>
                          </div>
                          <Progress value={p.progress} className="h-1.5" />
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="mt-8 text-xs text-muted-foreground italic border-t border-border pt-4">
        Sumber: Laporan Hasil RAKER BPK Klasis Mimika 22 Agustus 2026 — ditandatangani Pdt. Silwanus Werimon (Ketua Klasis).
      </div>
    </AppShell>
  );
}
